import { withApollo } from '@apollo/client/react/hoc';
import { Capacitor } from '@capacitor/core';
import classNames from 'classnames';
import { flow } from 'lodash';
import type { ReactElement } from 'react';
import { Suspense } from 'react';
import { useDispatch, useStore } from 'react-redux';

import AppRoutes from 'routes';

import withAnalytics, { CIAnalytics } from 'common/analytics/analytics.provider';
import PendoAnalytics from 'common/analytics/pendo';
import { AuthActions } from 'common/auth/actions';
import type { AuthState } from 'common/auth/types';
import type { UserSubType } from 'common/graphql/types';
import { useAutoRoleSync } from 'common/hooks/useAutoRoleSync';
import { useBrowserTabSync } from 'common/hooks/useBrowserTabSync';
import { useDetectPendoFailure } from 'common/hooks/useDetectPendoFailure';
import { AuditLoggerProvider } from 'common/log/ci.auditlogger';
import CIAppLogger from 'common/log/ci.logger';
import ConsoleLogger from 'common/log/console.logger';
import LoggerProvider, { CILogger } from 'common/log/log.provider';
import 'common/sentry/sentry';
import Telehealth from 'common/telehealth';
import type { ComponentWithoutHOCProps } from 'common/types/component';
import { SuspenseFallback } from 'common/utils/loading';
import CINetworkBanner from 'common/viewcomp/general/offline/network-banner/ci_network_banner';

import './app.scss';

interface PendoIdentity {
    visitor: {
        id: string;
        email: string;
        userCreation: Date;
        username: string;
        userType: string;
        subType: UserSubType;
        language: string;
        preferredLanguage: string;
        studyId: string;
        siteId: string;
        baseRole: string;
        roleVariant: string;
        device: string;
        operatingSystem: string;
        osVersion: string;
    };
    account: { id: string; name?: string; createDate?: string };
}

interface PendoGuide {
    name: string;
    id: string;
    hide: (options?: { stayHidden?: boolean }) => void;
    isComplete?: () => boolean;
}

interface PendoEvents {
    guidesLoaded?: (callback?: () => void) => void;
}

interface PendoIdentity {
    visitor: {
        id: string;
        email: string;
        userCreation: Date;
        username: string;
        userType: string;
        subType: UserSubType;
        language: string;
        preferredLanguage: string;
        studyId: string;
        siteId: string;
        baseRole: string;
        roleVariant: string;
        device: string;
        operatingSystem: string;
        osVersion: string;
    };
    account: { id: string; name?: string; createDate?: string };
}

interface PendoEvents {
    guidesLoaded?: (callback?: () => void) => void;
}

declare global {
    interface Window {
        pendo?: {
            visitorId: string;
            initialize: (pendoInit?: { events?: PendoEvents }) => void;
            identify: (identity: PendoIdentity) => void;
            isReady: () => boolean;
            clearSession: () => void;
            events?: PendoEvents;
            getActiveGuides?: () => PendoGuide[];
        };
    }
}

const analytics = CIAnalytics.getInstance();
analytics.registerProvider(new PendoAnalytics());

const logging = CILogger.getInstance();
if (process.env.NODE_ENV !== 'production') {
    logging.registerProvider(new ConsoleLogger());
}
logging.registerProvider(new CIAppLogger());
logging.initialize();

function App(): ReactElement {
    const store = useStore<{ auth: AuthState }>();
    const { auth } = store.getState();
    const dispatch = useDispatch();

    if (auth.activeUser?.pk) {
        // if pendo has not been initialized, initialize after auth user is set for the first time
        if (!window?.pendo?.isReady?.()) {
            CIAnalytics.getInstance().initialize(auth.activeUser);
        }

        // Update Pendo user identity if visitor id is undefined or visitor id is out of sync with auth user
        if (window.pendo?.visitorId !== (auth?.activeUser?.oldUserId || auth?.activeUser?.pk)) {
            CIAnalytics.getInstance().identifyUser(auth.activeUser);
        }
    }

    useBrowserTabSync();
    useDetectPendoFailure();
    useAutoRoleSync();

    return (
        <div
            id="app_container"
            className={classNames({
                'televisit_thumbnail': auth.thumbnailMode,
            })}
        >
            {auth.activeMeeting && (
                <Telehealth
                    {...(auth.meetingDetails || {})}
                    closeParent={(): void => {
                        dispatch(AuthActions.updateTelevisitStatus({ id: '', name: '', isAdmin: '' }, false));
                    }}
                />
            )}
            <Suspense fallback={<SuspenseFallback />}>
                {Capacitor.isNativePlatform() && <CINetworkBanner />}
                <AppRoutes />
            </Suspense>
        </div>
    );
}

const AppWrapper: ComponentWithoutHOCProps<{}> = flow([withApollo, withAnalytics, LoggerProvider, AuditLoggerProvider])(
    App
);

export default AppWrapper;
